import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  FileText,
  Download,
  CheckCircle2,
  Circle,
  ClipboardList,
  FileSpreadsheet,
  Upload,
  Send,
  RefreshCw,
  FileUp,
  ExternalLink,
  Calendar,
  Award,
  Clock,
  MessageSquare,
  Star,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from "sonner";

export default function CourseAssignments({ courseId }) {
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    completion_percentage: 0
  });
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [submittingAssignment, setSubmittingAssignment] = useState(null);
  const [submissionForm, setSubmissionForm] = useState({
    file_url: '',
    file_name: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mySubmissions, setMySubmissions] = useState({});

  const token = localStorage.getItem('faheem_token');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const loadAssignments = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/assignments/course/${courseId}?access_token=${token}`
      );

      if (response.data) {
        setAssignments(response.data.assignments || []);
        setStats({
          total: response.data.total || 0,
          completed: response.data.completed || 0,
          completion_percentage: response.data.completion_percentage || 0
        });
      }

      // Load submissions for this course
      const submissionsResponse = await axios.get(
        `${BACKEND_URL}/api/assignments/submissions/my?access_token=${token}&course_id=${courseId}`
      );
      
      if (submissionsResponse.data?.submissions) {
        const subsMap = {};
        submissionsResponse.data.submissions.forEach(sub => {
          subsMap[sub.assignment_id] = sub;
        });
        setMySubmissions(subsMap);
      }
    } catch (error) {
      console.error('Failed to load assignments:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load assignments');
      }
    } finally {
      setLoading(false);
    }
  }, [courseId, token, BACKEND_URL]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const toggleCompletion = async (assignmentId, currentStatus) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/assignments/toggle-completion?access_token=${token}`,
        {
          assignment_id: assignmentId,
          is_completed: !currentStatus
        }
      );

      if (response.data) {
        toast.success(response.data.message);
        loadAssignments();
      }
    } catch (error) {
      console.error('Failed to toggle completion:', error);
      toast.error('Failed to update completion status');
    }
  };

  const handleDownload = (fileUrl, title) => {
    window.open(fileUrl, '_blank');
    toast.success(`Opening ${title}`);
  };

  const handleSubmitAssignment = async () => {
    if (!submissionForm.file_url || !submissionForm.file_name) {
      toast.error('Please provide file URL and name');
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(
        `${BACKEND_URL}/api/assignments/submit?access_token=${token}`,
        {
          assignment_id: submittingAssignment.id,
          course_id: courseId,
          file_url: submissionForm.file_url,
          file_name: submissionForm.file_name,
          notes: submissionForm.notes || null
        }
      );

      toast.success('Assignment submitted successfully!');
      setSubmittingAssignment(null);
      setSubmissionForm({ file_url: '', file_name: '', notes: '' });
      loadAssignments();
    } catch (error) {
      console.error('Failed to submit assignment:', error);
      toast.error('Failed to submit assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openSubmitDialog = (assignment) => {
    const existingSubmission = mySubmissions[assignment.id];
    if (existingSubmission) {
      setSubmissionForm({
        file_url: existingSubmission.file_url || '',
        file_name: existingSubmission.file_name || '',
        notes: existingSubmission.notes || ''
      });
    } else {
      setSubmissionForm({ file_url: '', file_name: '', notes: '' });
    }
    setSubmittingAssignment(assignment);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusInfo = (assignment, submission) => {
    if (submission?.is_graded) {
      return {
        label: 'Graded',
        color: 'bg-green-100 text-green-700 border-green-200',
        icon: Award,
        bgColor: 'bg-green-50 border-green-200'
      };
    }
    if (submission) {
      return {
        label: 'Submitted - Pending Review',
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        icon: Clock,
        bgColor: 'bg-amber-50 border-amber-200'
      };
    }
    if (assignment.is_completed) {
      return {
        label: 'Completed',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: CheckCircle2,
        bgColor: 'bg-blue-50 border-blue-200'
      };
    }
    return {
      label: 'Not Started',
      color: 'bg-slate-100 text-slate-600 border-slate-200',
      icon: Circle,
      bgColor: 'bg-white border-slate-200'
    };
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-3">
            <RefreshCw className="w-5 h-5 animate-spin text-purple-600" />
            <span className="text-slate-600">Loading assignments...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-slate-200 shadow-sm">
      {/* Collapsible Header with Stats */}
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left"
      >
        <CardHeader className="pb-4 pt-5 px-5 border-b border-slate-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-slate-900">
                  Assignments & Worksheets
                </CardTitle>
                <CardDescription className="text-sm text-slate-500 mt-0.5">
                  Complete assignments to track your progress
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {stats.total > 0 && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-700">
                    {stats.completed}/{stats.total}
                  </div>
                  <div className="text-xs text-slate-500">Completed</div>
                </div>
              )}
              <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
            </div>
          </div>
        </CardHeader>
      </button>
      
      {isExpanded && (
      <>
        {/* Progress Bar */}
        {stats.total > 0 && (
          <div className="px-5 pt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-slate-700">Overall Progress</span>
              <span className={`text-sm font-bold ${
                stats.completion_percentage === 100 ? 'text-green-600' :
                stats.completion_percentage >= 75 ? 'text-slate-700' :
                stats.completion_percentage >= 50 ? 'text-slate-600' :
                stats.completion_percentage >= 25 ? 'text-orange-600' :
                'text-slate-600'
              }`}>
                {stats.completion_percentage}%
              </span>
            </div>
            <Progress 
              value={stats.completion_percentage} 
              className={`h-2.5 bg-slate-100 ${
                stats.completion_percentage === 100 ? '[&>div]:bg-green-500' :
                stats.completion_percentage >= 75 ? '[&>div]:bg-slate-700' :
                stats.completion_percentage >= 50 ? '[&>div]:bg-slate-600' :
                stats.completion_percentage >= 25 ? '[&>div]:bg-orange-500' :
                '[&>div]:bg-slate-400'
              }`}
            />
          </div>
        )}

      {/* Assignments List */}
      <CardContent className="px-5 py-4">
        {assignments.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Assignments Yet</h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              Assignments and worksheets for this course will appear here when added by your instructor.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {assignments.map((assignment, index) => {
              const submission = mySubmissions[assignment.id];
              const statusInfo = getStatusInfo(assignment, submission);
              const StatusIcon = statusInfo.icon;
              
              return (
                <div 
                  key={assignment.id}
                  className={`rounded-xl border-2 transition-all duration-200 hover:shadow-md ${statusInfo.bgColor}`}
                >
                  {/* Assignment Header */}
                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Completion Toggle */}
                      <button
                        onClick={() => toggleCompletion(assignment.id, assignment.is_completed)}
                        className="mt-1 flex-shrink-0 transition-transform hover:scale-110"
                      >
                        {assignment.is_completed ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                          <Circle className="w-6 h-6 text-slate-300 hover:text-purple-500 transition-colors" />
                        )}
                      </button>
                      
                      {/* Assignment Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <h3 className={`font-semibold text-base ${
                              assignment.is_completed ? 'line-through text-slate-500' : 'text-slate-900'
                            }`}>
                              {assignment.title}
                            </h3>
                            {assignment.description && (
                              <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                                {assignment.description}
                              </p>
                            )}
                          </div>
                          
                          {/* Type Badge */}
                          <Badge 
                            variant="outline" 
                            className={`flex-shrink-0 ${
                              assignment.assignment_type === 'worksheet' 
                                ? 'border-blue-300 text-blue-700 bg-blue-50' 
                                : 'border-purple-300 text-purple-700 bg-purple-50'
                            }`}
                          >
                            {assignment.assignment_type === 'worksheet' ? (
                              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
                            ) : (
                              <FileText className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            {assignment.assignment_type === 'worksheet' ? 'Worksheet' : 'Assignment'}
                          </Badge>
                        </div>
                        
                        {/* Meta Info Row */}
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                          {/* Status */}
                          <Badge className={`${statusInfo.color}`}>
                            <StatusIcon className="w-3.5 h-3.5 mr-1.5" />
                            {statusInfo.label}
                          </Badge>
                          
                          {/* Points */}
                          {assignment.points > 0 && (
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Star className="w-4 h-4 text-amber-500" />
                              <span className="font-medium">{assignment.points} pts</span>
                            </div>
                          )}
                          
                          {/* Due Date */}
                          {assignment.due_date && (
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span>Due: {formatDate(assignment.due_date)}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Grade Display */}
                        {submission?.is_graded && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-green-600" />
                                <span className="font-medium text-green-800">Your Grade</span>
                              </div>
                              <span className="text-2xl font-bold text-green-600">
                                {submission.grade}<span className="text-base text-green-500">/100</span>
                              </span>
                            </div>
                            {submission.feedback && (
                              <div className="mt-2 pt-2 border-t border-green-200">
                                <div className="flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs font-medium text-green-700 mb-1">Teacher Feedback</p>
                                    <p className="text-sm text-green-800">{submission.feedback}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Submission Info */}
                        {submission && !submission.is_graded && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileUp className="w-4 h-4 text-amber-600" />
                              <span className="text-sm text-amber-800">
                                Submitted: <span className="font-medium">{submission.file_name}</span>
                              </span>
                            </div>
                            {submission.notes && (
                              <p className="text-sm text-amber-700 mt-1 ml-6">{submission.notes}</p>
                            )}
                            <p className="text-xs text-amber-600 mt-1 ml-6">
                              Submitted on {formatDate(submission.created_at)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-4 ml-10">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(assignment.file_url, assignment.title)}
                        className="h-9 px-4 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      
                      <Button
                        size="sm"
                        onClick={() => openSubmitDialog(assignment)}
                        className={`h-9 px-4 ${
                          submission 
                            ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                        }`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {submission ? 'Resubmit' : 'Submit Work'}
                      </Button>
                      
                      {submission?.file_url && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(submission.file_url, '_blank')}
                          className="h-9 px-3 text-slate-600 hover:text-slate-900"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Submission
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Submit Assignment Dialog */}
      <Dialog open={!!submittingAssignment} onOpenChange={() => setSubmittingAssignment(null)}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <span className="text-lg">Submit Assignment</span>
                <p className="text-sm font-normal text-slate-500 mt-0.5">
                  {submittingAssignment?.title}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                How to Submit
              </h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Upload your file to Google Drive, Dropbox, or OneDrive</li>
                <li>Make sure the file is shared/accessible</li>
                <li>Copy the file link and paste it below</li>
              </ol>
            </div>

            <div>
              <Label htmlFor="file_url" className="text-sm font-medium text-slate-700">
                File URL <span className="text-red-500">*</span>
              </Label>
              <Input
                id="file_url"
                value={submissionForm.file_url}
                onChange={(e) => setSubmissionForm(prev => ({ ...prev, file_url: e.target.value }))}
                placeholder="https://drive.google.com/file/d/..."
                className="mt-1.5 bg-white"
                autoComplete="off"
              />
            </div>

            <div>
              <Label htmlFor="file_name" className="text-sm font-medium text-slate-700">
                File Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="file_name"
                value={submissionForm.file_name}
                onChange={(e) => setSubmissionForm(prev => ({ ...prev, file_name: e.target.value }))}
                placeholder="my-assignment.pdf"
                className="mt-1.5 bg-white"
                autoComplete="off"
              />
            </div>

            <div>
              <Label htmlFor="notes" className="text-sm font-medium text-slate-700">
                Notes for Teacher <span className="text-slate-400">(Optional)</span>
              </Label>
              <Textarea
                id="notes"
                value={submissionForm.notes}
                onChange={(e) => setSubmissionForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes or comments about your submission..."
                rows={3}
                className="mt-1.5 bg-white"
                autoComplete="off"
              />
            </div>

            {mySubmissions[submittingAssignment?.id] && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Previous Submission</h4>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">
                      {mySubmissions[submittingAssignment?.id]?.file_name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDate(mySubmissions[submittingAssignment?.id]?.created_at)}
                    </p>
                  </div>
                  {mySubmissions[submittingAssignment?.id]?.is_graded && (
                    <Badge className="bg-green-100 text-green-700">
                      Grade: {mySubmissions[submittingAssignment?.id]?.grade}/100
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSubmittingAssignment(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitAssignment} 
              disabled={isSubmitting || !submissionForm.file_url || !submissionForm.file_name}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Assignment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </Card>
  );
}
